/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Text attached to a control rather than to a `?`** — `X28b` as amended 2026-09-08, swept
 * console-wide in `#1276`.
 *
 * The console has two hover affordances and they answer different questions. {@link FieldHelpTooltip}
 * is a `?` **beside a label**: the operator asks for it, and it explains what a control is for.
 * This one hangs on the **control itself**, so the operator gets it by reaching for a control that
 * will not move — which is the case a `?` cannot serve, because a refusal is read by somebody who
 * has already tried.
 *
 * ## Why the reason moved off the surface
 *
 * `X28b` originally ruled the opposite: *a disabled control whose reason is behind a hover is an
 * absent control again*, which is true of **one** control on **one** screen. `E2`'s case ladder
 * renders the same chip row four times at once and turned it into eight lines of standing prose
 * saying the same two things — at which point it stops being a statement and becomes wallpaper,
 * which is the outcome `X28b` was written to prevent, reached from the other side (`#1248`: state a
 * claim once, at the level it is true at). Todd ratified the amendment after using the trial:
 * *"Help & disabled reason on hover, warnings/errors on the surface feels right."*
 *
 * **The line this draws, and it is the whole rule:**
 *
 * | | Where |
 * | --- | --- |
 * | what this control is for | hover (`?`) |
 * | why this control will not move **here** | hover (**this**) |
 * | a warning or an error about the **value** | on the surface, always visible |
 *
 * A warning is about something the operator can act on and may not have noticed. A refusal is about
 * something they are already looking at, having just tried it.
 *
 * ## What is measured, and what is not — stated separately on purpose
 *
 * The claim *a disabled control swallows pointer events, so the reason can never be reached* is the
 * well-known one, it is what `aria-disabled` is usually reached for, and it was written into
 * `#1276` as load-bearing **without being checked**. So the record here is deliberately narrow:
 *
 * | Control | Through this tooltip |
 * | --- | --- |
 * | `<button>` (a capability chip) | **measured hoverable, 2026-09-08 in Chromium** — `mouseenter` fires on the disabled button and the tooltip opens |
 * | `<input>`, `<select>`, a checkbox | **measured 2026-09-09 in the `#1276` rehearsal.** Each opens; the checkbox opened in the **wrong place**, which is a different fault and is fixed below |
 *
 * So the folklore is wrong for every control type this console disables, and it is now measured
 * rather than inferred. What the rehearsal did find is that **`asChild` on a composite root loses
 * the positioning ref** — see the trigger below.
 *
 * ## The reason stays in the accessible tree, and that is not optional
 *
 * A tooltip renders its content **only while open**, and a `disabled` control **cannot take
 * keyboard focus** — so a reason that lives only in the hover is reachable by a mouse and by
 * nothing else. Moving it off the surface for density would otherwise have deleted it outright for
 * anyone using a screen reader or a keyboard, which is a bigger loss than the standing prose was a
 * cost.
 *
 * So the text is **also** rendered visually hidden and bound to the control with
 * `aria-describedby`. Sighted mouse users get the hover; everyone else gets the sentence where the
 * control is. It is the same claim in one place, rendered for two audiences — not the *two
 * mechanisms for one claim* this sweep removed, which were two **visible** ones competing on screen.
 *
 * It is also what keeps the assertion honest: the eight specs that asserted the reason was on the
 * surface now assert it is *attached to the control*, which is a real property rather than the
 * absence of one. A sweep that had simply deleted them would be *a gate satisfied by doing less*.
 *
 * ## It never renders when there is nothing to say
 *
 * `text` is **required and nullable**, never optional (`#1134`): a surface with no reason passes
 * `null` and gets its control back untouched, and a surface that forgot cannot compile. The wrapper
 * disappearing rather than rendering an empty tooltip is what keeps *hover and nothing happens* off
 * the screen.
 */

import React from 'react';
import { chakra, Portal, Tooltip, VisuallyHidden } from '@chakra-ui/react';

export interface ControlHoverProps {
  /**
   * What the hover says. `null` renders the child alone — no trigger, no empty tooltip.
   *
   * **On a refused control this is the refusal, not the help.** Showing both was two tooltips on
   * one control (the native `title` under the Chakra one), and the generic help answers a question
   * the operator has stopped asking the moment the control will not move.
   */
  text: string | null;
  children: React.ReactNode;
  /**
   * **The control fills its row rather than sizing to its content.** Optional with a correct
   * default, and it says why: the wrapper below becomes the flex child, so a control that was
   * `flex="1"` would otherwise collapse to its intrinsic width. Only the Note input needs it, and
   * getting it wrong is visible immediately — unlike a forgotten capability, which is what the
   * required-and-nullable rule is for.
   */
  grow?: boolean;
}

export const ControlHover: React.FC<ControlHoverProps> = ({ text, children, grow = false }) => {
  const id = React.useId();
  if (!text) return <>{children}</>;

  // Bound to the control rather than merely adjacent to it: document order puts the sentence after
  // the control either way, but only the association survives a screen reader jumping straight to
  // the control it is about.
  const described = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<{ 'aria-describedby'?: string }>, {
        'aria-describedby': id,
      })
    : children;

  return (
    <>
      <Tooltip.Root openDelay={200} closeDelay={120}>
        {/**
         * **The trigger is an element this component owns, not the control itself.**
         *
         * `asChild` on the control works for a plain DOM element or a styled primitive, which
         * forward their ref natively — and **silently fails to position** on a composite root that
         * composes its own. Reported in rehearsal: the *Accept system default* checkbox's hover
         * opened in the top-left corner of the viewport, because the positioner had no measurable
         * anchor. The merge itself was fine, which is what makes it hard to see — the trigger's
         * props land on the label and only the **ref** is lost.
         *
         * Anchoring to a wrapper makes that unrepresentable rather than something each call site
         * has to know about its own child. *Which of my children forward refs* is exactly the
         * per-caller knowledge that produces the next instance of this.
         */}
        <Tooltip.Trigger asChild>
          <chakra.div
            display="inline-flex"
            alignItems="center"
            /* So a child sized against *the row* still is: the wrapper sits between them, and a
               chip that truncates at `maxW="100%"` would otherwise measure itself against a
               wrapper that has already grown to fit it. */
            maxW="100%"
            flex={grow ? '1' : undefined}
            minW={grow ? 0 : undefined}
          >
            {described}
          </chakra.div>
        </Tooltip.Trigger>
        <Portal>
          <Tooltip.Positioner>
            <Tooltip.Content bg="bg.nav" color="white" fontSize="xs" maxW="280px" p={3} borderRadius="6px">
              {text}
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Portal>
      </Tooltip.Root>
      {/* **Marked so a test can tell hidden from standing.** The assertion this replaces was
          `not.toContain('>' + sentence)` — element text anywhere meant the prose was still on the
          surface — and it cannot distinguish *hidden for the screen reader* from *visible on the
          row*. Both are element text. The marker is what makes the property assertable
          structurally, which is the same fix `X29`'s second instance needed. */}
      <VisuallyHidden id={id} data-hover-text="true">
        {text}
      </VisuallyHidden>
    </>
  );
};
